import {
  Box,
  Flex,
  Stack,
  Heading,
  Text,
  Container,
  Link,
  Button,
  Icon,
  useColorModeValue,
  useDisclosure,
  IconButton,
  HStack,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorMode,
} from '@chakra-ui/react'
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom'
import { FiHome, FiClock, FiMenu, FiLogOut, FiMoon, FiSun, FiUser } from 'react-icons/fi'
import { useAuth } from '../../context/AuthContext'

const MainLayout = () => {
  const { logout } = useAuth()
  const location = useLocation()
  const { isOpen, onToggle } = useDisclosure()
  const { colorMode, toggleColorMode } = useColorMode()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'white')
  const sidebarShadow = useColorModeValue('lg', 'dark-lg')

  const NavItem = ({ to, icon, children }: { to: string; icon: any; children: React.ReactNode }) => {
    const isActive = location.pathname === to
    return (
      <Link
        as={RouterLink}
        to={to}
        w="full"
        _hover={{ textDecoration: 'none' }}
      >
        <Flex
          align="center"
          p="4"
          mx="4"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          bg={isActive ? 'brand.50' : 'transparent'}
          color={isActive ? 'brand.700' : textColor}
          _hover={{
            bg: useColorModeValue('gray.100', 'gray.700'),
            color: useColorModeValue('brand.600', 'brand.300'),
          }}
          transition="all 0.2s"
        >
          <Icon
            mr="4"
            fontSize="18"
            as={icon}
            color={isActive ? 'brand.500' : 'gray.500'}
            _groupHover={{
              color: useColorModeValue('brand.500', 'brand.300'),
            }}
          />
          <Text fontWeight={isActive ? "600" : "normal"}>{children}</Text>
        </Flex>
      </Link>
    )
  }

  return (
    <Box minH="100vh">
      {/* Mobile nav overlay */}
      {isOpen && (
        <Box 
          position="fixed" 
          top="0" 
          left="0" 
          right="0" 
          bottom="0" 
          bg="blackAlpha.600" 
          zIndex="overlay" 
          display={{ base: 'block', md: 'none' }}
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <Box
        bg={bgColor}
        borderRight="1px"
        borderRightColor={borderColor}
        w={{ base: 'full', md: 60 }}
        pos="fixed"
        h="full"
        display={{ base: isOpen ? 'block' : 'none', md: 'block' }}
        zIndex="modal"
        boxShadow={{ base: sidebarShadow, md: 'none' }}
        transition="all 0.3s"
      >
        <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
          <HStack spacing={2}>
            <Box 
              bg="brand.500" 
              w="8" 
              h="8" 
              borderRadius="md" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
            >
              <Text color="white" fontWeight="bold" fontSize="lg">D</Text>
            </Box>
            <Heading fontSize="xl" fontWeight="bold" color={textColor}>
              Dyno Manager
            </Heading>
          </HStack>
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onToggle}
            variant="ghost"
            aria-label="close menu"
            icon={<FiMenu />}
            size="sm"
          />
        </Flex>
        <Divider borderColor={borderColor} />
        <Box py={4}>
          <Stack spacing={1}>
            <NavItem to="/" icon={FiHome}>
              Dashboard
            </NavItem>
            <NavItem to="/schedules" icon={FiClock}>
              Schedules
            </NavItem>
          </Stack>
        </Box>
      </Box>

      {/* Main content */}
      <Box ml={{ base: 0, md: 60 }} p={0}>
        {/* Header */}
        <Flex
          bg={bgColor}
          borderBottomWidth="1px"
          borderBottomColor={borderColor}
          boxShadow="sm"
          position="sticky"
          top="0"
          zIndex="sticky"
          h="16"
          alignItems="center"
          justifyContent="space-between"
          px={{ base: 4, md: 6 }}
        >
          <HStack spacing={4}>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              onClick={onToggle}
              variant="outline"
              aria-label="open menu"
              icon={<FiMenu />}
              size="sm"
            />
            <Text fontSize="xl" fontWeight="bold" color={textColor}>
              {location.pathname === '/' ? 'Dashboard' : 'Schedules'}
            </Text>
          </HStack>
          <HStack spacing={3}>
            <IconButton
              aria-label={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} mode`}
              variant="ghost"
              icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
              onClick={toggleColorMode}
              size="sm"
            />
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                rightIcon={<FiUser />}
              >
                Account
              </MenuButton>
              <MenuList>
                <MenuItem icon={<FiLogOut />} onClick={logout}>
                  Logout
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* Page content */}
        <Box p={{ base: 4, md: 6 }} bg={useColorModeValue('gray.50', 'gray.900')}>
          <Container maxW="container.xl" p={0}>
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default MainLayout 